import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { GetPaginatedUsersQueryVariables, Organization } from "@/graphql/__generated__/graphql";
import { usePaginationState, updatePaginationState } from "@/store/paginationStore";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { CheckIcon, RefreshCcwIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { CustomCombobox } from "@/components/shared";

interface Props {
  onFilterSubmit: (_variables: GetPaginatedUsersQueryVariables) => void;
  displayAmount: number;
  organizationsData: Organization[];
  organizationsLoading: boolean;
}

const UsersFilter = (props: Props) => {

  // Props
  const { onFilterSubmit, displayAmount, organizationsLoading, organizationsData } = props;

  const { t } = useTranslation(["components", "common"]);

  // Pagination
  const paginationState: PaginationState["paginatedUsers"] = usePaginationState("paginatedUsers");
  const { filters } = paginationState;

  /**
   * OnClick handler for Reset button
   */
  const handleResetClick = () => {

    // Update state
    updatePaginationState({
      paginatedUsers: {
        filters: {
          name: "",
          organizationId: null,
        }
      }
    });

    // Call parent function to update query
    onFilterSubmit({
      first: displayAmount,
      filters: {
        name: "",
        organizationId: null,
      }
    });
  };

  /**
   * OnClick handler for Submit button
   */
  const handleSubmitClick = () => {
    onFilterSubmit({
      first: displayAmount,
      filters: {
        name: filters?.name,
        organizationId: filters?.organizationId,
      }
    });
  };

  return (
    <div className="mb-1 -mt-4 pt-4">
      <div className="flex flex-wrap items-center justify-between">
        {/* Date and Financing Status Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-4 w-full">
          {/* Left container --> User name */}
          <div className="flex-1 max-md:w-full">
            <Input
              data-cy="name-input"
              value={filters?.name ?? ""}
              type="text"
              onChange={(event) => updatePaginationState({ paginatedUsers: { filters: { name: event.target.value } } })}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleSubmitClick();
                }
              }}
              placeholder={t("filters.users.searchplaceholder")}
            />
          </div>

          {/* Center container --> Organization ID */}
          <div className="flex-1 max-w-[300px]">
            <CustomCombobox
              value={filters?.organizationId?.toString() ?? ""}
              onChange={(value) => {
                const numericValue = value ? Number(value) : null;
                updatePaginationState({ paginatedUsers: { filters: { organizationId: numericValue } } });
              }}
              options={organizationsData.map(org => ({
                value: String(org.id),
                label: org.name
              }))}
              placeholder={t("filters.users.selectplaceholder")}
              loading={organizationsLoading}
              data-cy="organization-id-select"
            />
          </div>

          {/* Right container --> Filter box */}
          <div className="flex flex-1 w-full items-center justify-between">
            {/* Right container --> Action buttons */}
            <div className="flex flex-1 justify-end gap-2 ml-2">
              {/* Reset button */}
              <Button variant="outline" size="icon" onClick={() => handleResetClick()}>
                <RefreshCcwIcon className="w-4 h-4" />
              </Button>

              {/* Reset button */}
              <Button
                variant={"outline"}
                data-cy="reset-button"
                onClick={handleResetClick}
              >
                <ArrowPathIcon className="icon-sm md:hidden" />
                <div className="hidden md:flex">
                  {t("common:buttons.reset")}
                </div>
              </Button>

              {/* Submit button */}
              <Button
                variant={"default"}
                data-cy="submit-button"
                onClick={handleSubmitClick}
              >
                <CheckIcon className="icon-sm md:hidden" />
                <div className="hidden md:flex">
                  {t("common:buttons.submit")}
                </div>
              </Button>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default UsersFilter;
